import React from "react";
import { ImageFluid } from "../../../../../../common-deprecated/styles/v2/toyota/globals/Image";
import type {
    ModelFilterGradeType,
    ModelGradeType,
    PricePromotionType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import { EcoTagType } from "../../../../../../common-deprecated/types/CommonTypes";
import * as Flex from "../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useBnbSelector } from "../../../redux/store";
import * as Text from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import * as Type from "../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Tooltip from "../../../../../../common-deprecated/styles/v2/toyota/components/Tooltip";
import { SrOnly } from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import Icon, { IconTradeIn } from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { BnbLabel, useBnbOptionalLabel } from "../../../utils/labels";
import { compareAddCar } from "../../../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import EcoTag from "../common/EcoTag";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import * as GradeItem from "./styles/Grade";
import Price from "../common/price/Price";
import { getGrLogo } from "../../../../../../common-deprecated/utils";
import { IconWrapper } from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { GrCode } from "../../../../../../common-deprecated/constants";
import GRLogo from "../../../../../../../assets/toyota/svg/GR/TME-GR.svg";
import GRLogoSport from "../../../../../../../assets/toyota/svg/GR/TME-GR-sport-black.svg";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_CONTENT } from "../../../../../../common-deprecated/utils/modalConstants";
import { trackAddCompare, trackNavGoToStep } from "../../../utils/tracking";
import { getEcoBonusPromotions } from "../../../../../../common-deprecated/utils/promotionUtils";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/EcoBonusPromotion";
import { useLoadImage, useUniqueId } from "../../../../../../common-deprecated/hooks";
import IconPlaceholder from "../../../../../../common-deprecated/svg/toyota/aem/placeholder-accessories.svg";
import ButtonPrimary from "../../../../../../common-deprecated/components/aem/toyota/ButtonPrimary";
import ButtonQuaternary from "../../../../../../common-deprecated/components/aem/toyota/ButtonQuaternary";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/toyota/availability-badges/AvailabilityBadges";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import * as Tag from "../../../../../../common-deprecated/styles/toyota/Tag";
import { useCommonOptionalLabel } from "../../../../../../common-deprecated/utils/commonLabels";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type ModelProps = {
    getGradeImage: (width: number, length: number, gradeId: string, bodyTypeId: string) => string;
    grade: ModelGradeType | ModelFilterGradeType;
    modelId: string;
    modelName: string;
};

const Grade = (props: ModelProps): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const { getGradeImage, grade, modelId, modelName } = props;
    const uniqueId = useUniqueId();
    const { ecoTag, motorizations, availableForOnlineSales, availableForOnlineLeasing } = grade as ModelGradeType;
    const model = useBnbSelector((state) => state.model);
    const ecoBonus = useBnbSelector((state) =>
        getEcoBonusPromotions<PricePromotionType>(state.items.promotions, grade.price.promotions),
    );
    const grLogoCheck = getGrLogo(modelName, grade.name);
    const [noAccesoriesDisclaimer, genericDisclaimerTitle] = useBnbOptionalLabel([
        "genericModelDisclaimer",
        "genericDisclaimerTitle",
    ]);
    const multipleEngineOptionsLabel = useCommonOptionalLabel("multipleEngineOptions");

    // if all motorizations are sold out, the grade is sold out
    const isSoldOut = motorizations?.every(({ soldOut }) => soldOut);

    const src = getGradeImage(464, 278, grade.id, grade.bodyType.id);
    const srcSet = `${getGradeImage(464, 278, grade.id, grade.bodyType.id)} 1x, ${getGradeImage(
        928,
        556,
        grade.id,
        grade.bodyType.id,
    )}`;

    const { loading, loadFailed } = useLoadImage(true, src, srcSet);

    const engineStep = useStepUrl(
        NavStep.Engine,
        {
            modelId,
            bodyTypeId: grade.bodyType.id,
            gradeId: grade.id,
            carId: (grade as ModelGradeType).defaultCarId || (grade as ModelFilterGradeType).carId,
        },
        undefined,
        () => trackNavGoToStep(NavStep.Grades, NavStep.Engine),
    );

    const hasMultipleEngineTypes =
        new Set(motorizations?.map((motorization) => motorization.engine?.type).filter(Boolean)).size > 1;

    return (
        <Flex.Col width={{ xs: 1, sm: 1 / 2, lg: 1 / 3 }} mb={4}>
            <GradeItem.Wrapper>
                <div>
                    <GradeItem.ImageWrapper>
                        {grLogoCheck && (
                            <GradeItem.GRLogo>
                                {grLogoCheck === GrCode.Gr ? <GRLogo /> : <GRLogoSport />}
                            </GradeItem.GRLogo>
                        )}
                        {loading ||
                            (loadFailed && (
                                <GradeItem.Placeholder>
                                    <IconPlaceholder />
                                </GradeItem.Placeholder>
                            ))}
                        {!loadFailed && <ImageFluid src={src} srcSet={srcSet} alt={`${model.name} ${grade.name}`} />}
                    </GradeItem.ImageWrapper>
                    <GradeItem.Name dir="ltr">
                        <Type.Strong as="h2" mb={1}>
                            {grade.name}
                        </Type.Strong>
                        <Type.SmallText>{grade.bodyType.name}</Type.SmallText>
                        {hasMultipleEngineTypes && multipleEngineOptionsLabel ? (
                            <Tag.Motorisation type={EcoTagType.Hybrid}>
                                <BnbLabel label="multipleEngineOptions" />
                            </Tag.Motorisation>
                        ) : (
                            !hasMultipleEngineTypes && ecoTag && <EcoTag type={ecoTag} />
                        )}
                    </GradeItem.Name>
                    <GradeItem.Price>
                        <EcoBonusPromotion ecoBonus={ecoBonus} />
                        <Price
                            price={grade.price}
                            financeInfo={grade.finance}
                            carInternalCode={model.internalCode}
                            cashPriceDisclaimer={model.labels}
                        />
                        {!!noAccesoriesDisclaimer && (
                            <Tooltip.TooltipButton
                                type="button"
                                onClick={() =>
                                    dispatch(showModal({ type: MODAL_CONTENT, content: noAccesoriesDisclaimer }))
                                }
                            >
                                {genericDisclaimerTitle}
                                <Icon variant="info" />
                            </Tooltip.TooltipButton>
                        )}
                    </GradeItem.Price>

                    <AvailabilityBadges
                        isSoldOut={isSoldOut}
                        isAvailableForOnlineSales={availableForOnlineSales}
                        isAvailableForOnlineLeasing={availableForOnlineLeasing}
                    />
                </div>

                <GradeItem.ButtonWrapper>
                    <ButtonPrimary
                        fullWidth
                        href={engineStep.href}
                        onClick={engineStep.onClick}
                        aria-labelledby={uniqueId}
                    >
                        <SrOnly id={uniqueId}>
                            {modelName} {grade.name} {grade.bodyType.name}:
                        </SrOnly>
                        <BnbLabel label="selectGrade" />
                    </ButtonPrimary>
                    <GradeItem.CompareWrapper>
                        <ButtonQuaternary
                            onClick={(event) => {
                                event.preventDefault();
                                trackAddCompare(NavStep.Grades);
                                const ids = {
                                    model: modelId,
                                    gradeId: grade.id,
                                    bodyTypeId: grade.bodyType.id,
                                    submodelId: "",
                                };
                                dispatch(compareAddCar(ids, null, -1));
                            }}
                            hasIcon
                        >
                            <SrOnly>
                                {modelName} {grade.name} {grade.bodyType.name}:
                            </SrOnly>
                            <Text.Button>
                                <BnbLabel label="compareAdd" />
                            </Text.Button>
                            <IconWrapper>
                                <IconTradeIn />
                            </IconWrapper>
                        </ButtonQuaternary>
                    </GradeItem.CompareWrapper>
                </GradeItem.ButtonWrapper>
            </GradeItem.Wrapper>
        </Flex.Col>
    );
};

export default Grade;
